import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AdminThemeProvider, useAdminTheme } from "../context/ThemeContext";
import { SettingsActionsProvider, useSettingsActionsBar } from "../context/SettingsActionsContext";
import { hasMenuPerm } from "../lib/menuAcl";
import { roleLabel, roleTintClass, isSysAdmin } from "../lib/roles";
import SiteFooter from "./SiteFooter";
import {
  LayoutDashboard, ClipboardList, Car, Settings as SettingsIcon, MapPin, Users,
  LogOut, Sun, Moon, ArrowUpRight, CalendarDays, Tag, Warehouse, Settings2, Radar, Package, Inbox, UsersRound,
  Activity, FileBarChart, ServerCog, Save, Check,
} from "lucide-react";

const ADMIN_TIER = ["admin", "super_admin"];

function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { dark, toggle } = useAdminTheme();
  const isAdmin = ADMIN_TIER.includes(user?.role);
  const sysAdmin = isSysAdmin(user);

  // Pull the operator's brand name (edited in Rental Settings → Company /
  // Brand) so the admin console header is white-label out of the box.
  const [brand, setBrand] = useState({ name: "", version: "" });
  useEffect(() => {
    api.get("/settings/public")
      .then((r) => setBrand({
        name: r.data?.brand_name || r.data?.company_name || "",
        version: r.data?.app_version || "",
      }))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className={dark ? "dark" : ""} data-testid="admin-shell">
      <div className="min-h-screen flex bg-[#F3F4F6] dark:bg-[#111827] text-[#0A0A0A] dark:text-[#F9FAFB] transition-colors">
        {/* Sidebar */}
        {/* v4.6.0 — desktop sidebar is its own sticky, full-height column so the
            nav scrolls internally and the logout footer is always reachable. */}
        <aside
          className="hidden lg:flex lg:sticky lg:top-0 lg:h-screen w-64 shrink-0 flex-col justify-between border-r border-black/8 dark:border-white/10 bg-white dark:bg-[#1F2937]"
          data-testid="admin-sidebar"
        >
          <div className="px-6 py-7 shrink-0" data-testid="admin-brand">
            <p className="font-display text-2xl tracking-tight leading-tight" data-testid="admin-brand-name">
              {brand.name || "Brainart Rent"}
            </p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400 mt-1">
              Car Rental App{brand.version ? ` · ${brand.version}` : ""} / Admin Console
            </p>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto px-3 space-y-1">
            <SidebarLink to="/admin" icon={<LayoutDashboard size={16} />} label="Dashboard" end testId="nav-dashboard" requireAdmin />
            <SidebarLink to="/admin/bookings" icon={<ClipboardList size={16} />} label="Bookings" testId="nav-bookings" menu="bookings" />
            <SidebarLink to="/admin/inquiries" icon={<Inbox size={16} />} label="Inquiries" testId="nav-inquiries" menu="inquiries" />
            <SidebarLink to="/admin/clients" icon={<UsersRound size={16} />} label="Clients" testId="nav-clients" menu="clients" />
            <SidebarLink to="/admin/locations" icon={<MapPin size={16} />} label="Locations" testId="nav-locations" menu="locations" />
            <SidebarLink to="/admin/calendar" icon={<CalendarDays size={16} />} label="Calendar" testId="nav-calendar" menu="calendar" />
            <SidebarLink to="/admin/fleet" icon={<Car size={16} />} label="Car Groups" testId="nav-fleet" menu="car_groups" />
            <SidebarLink to="/admin/car-fleet" icon={<Warehouse size={16} />} label="Car Fleet" testId="nav-car-fleet" menu="car_fleet" />
            <SidebarLink to="/admin/car-extras" icon={<Package size={16} />} label="Car Extras" testId="nav-car-extras" menu="car_extras" />
            <SidebarLink to="/admin/fleet-radar" icon={<Activity size={16} />} label="Fleet Radar" testId="nav-fleet-radar" menu="fleet_radar" />
            <SidebarLink to="/admin/vouchers" icon={<Tag size={16} />} label="Vouchers" testId="nav-vouchers" menu="vouchers" />
            <SidebarLink to="/admin/price-radar" icon={<Radar size={16} />} label="Price Radar" testId="nav-price-radar" menu="price_radar" />
            <SidebarLink to="/admin/reports" icon={<FileBarChart size={16} />} label="Reports" testId="nav-reports" menu="reports" />
            <SidebarLink to="/admin/rental-settings" icon={<Settings2 size={16} />} label="Rental Settings" testId="nav-rental-settings" menu="rental_settings" />
            <SidebarLink to="/admin/business" icon={<SettingsIcon size={16} />} label="Business Settings" testId="nav-business" menu="business_settings" />
            <SidebarLink to="/admin/system-settings" icon={<ServerCog size={16} />} label="System Settings" testId="nav-system-settings" menu="system_settings" />
            {sysAdmin && (
              <SidebarLink to="/admin/users" icon={<Users size={16} />} label="User Management" testId="nav-users" />
            )}
          </nav>

          <div
            className="mt-auto shrink-0 p-3 border-t border-black/8 dark:border-white/10 bg-white dark:bg-[#1F2937]"
            data-testid="admin-sidebar-footer"
          >
            <div className="px-3 py-2 mb-2">
              <p className="text-xs text-neutral-500 dark:text-neutral-400">Signed in as</p>
              <p className="text-sm font-medium truncate">{user?.email}</p>
              <span
                className={`inline-flex mt-1.5 text-[10px] px-1.5 py-0.5 rounded border ${roleTintClass(user?.role)}`}
                data-testid="sidebar-role-badge"
              >
                {roleLabel(user?.role)}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Topbar */}
          <header
            className="sticky top-0 z-30 flex items-center justify-between px-6 lg:px-10 py-4 border-b border-black/8 dark:border-white/10 backdrop-blur-xl bg-[#F3F4F6]/80 dark:bg-[#111827]/70"
            data-testid="admin-topbar"
          >
            <div className="flex items-center gap-3">
              <span className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">
                {isAdmin ? "Admin" : "Staff"} · Operations
              </span>
            </div>
            <div className="flex items-center gap-2">
              <SettingsTopbarActions />
              <a
                href="/book"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:inline-flex items-center gap-1 text-xs px-3 py-1.5 border border-black/10 dark:border-white/15 hover:border-black dark:hover:border-white transition-colors rounded"
                data-testid="admin-view-site"
              >
                View site <ArrowUpRight size={12} />
              </a>
              <button
                onClick={toggle}
                aria-label="Toggle dark mode"
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-black/10 dark:border-white/15 hover:border-black dark:hover:border-white transition-colors rounded text-xs"
                data-testid="dark-mode-toggle"
              >
                {dark ? <Sun size={14} /> : <Moon size={14} />}
                <span className="hidden sm:inline">{dark ? "Light" : "Dark"}</span>
              </button>
            </div>
          </header>

          <main className="flex-1 px-6 lg:px-10 py-8">{children}</main>
          <SiteFooter variant="admin" />
        </div>
      </div>
    </div>
  );
}

function SettingsTopbarActions() {
  const actions = useSettingsActionsBar();
  if (!actions || (!actions.dirty && !actions.saved && !actions.saving)) return null;
  const { dirty, saving, saved, save, discard } = actions;
  return (
    <div
      className="flex items-center gap-3 mr-1 animate-in fade-in slide-in-from-top-1 duration-200"
      data-testid="topbar-settings-actions"
    >
      {dirty && !saved && (
        <button
          type="button"
          onClick={discard}
          className="text-xs text-neutral-600 dark:text-neutral-400 underline underline-offset-4 hover:text-black dark:hover:text-white transition-colors"
          data-testid="discard-settings"
        >
          Discard
        </button>
      )}
      <button
        type="button"
        onClick={save}
        disabled={saving || saved}
        className={`inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded transition-colors disabled:opacity-100 ${
          saved
            ? "bg-emerald-600 text-white"
            : "bg-black text-white dark:bg-white dark:text-black hover:opacity-90"
        }`}
        data-testid="save-settings"
      >
        {saved ? <Check size={13} /> : <Save size={13} />}
        {saved ? "Saved" : saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

function SidebarLink({ to, icon, label, end, testId, requireAdmin, requirePerm, menu }) {
  const { user } = useAuth();
  if (requireAdmin && !ADMIN_TIER.includes(user?.role)) return null;
  // v4.6.8 — granular 1-to-1 menu permission (SysAdmin always passes).
  if (menu && !hasMenuPerm(user, menu, "view")) return null;
  if (requirePerm) {
    if (user?.role === "super_admin") {
      // full access — always visible
    } else if (ADMIN_TIER.includes(user?.role)) {
      const acl = user?.acl || {};
      const has = !!(acl?.[requirePerm.section]?.[requirePerm.action]);
      if (!has) return null;
    } else {
      // customers never see admin sidebar entries
      return null;
    }
  }
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors ${
          isActive
            ? "bg-black text-white dark:bg-white dark:text-black"
            : "hover:bg-black/5 dark:hover:bg-white/5 text-neutral-700 dark:text-neutral-300"
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default function AdminLayout({ children }) {
  return (
    <AdminThemeProvider>
      <SettingsActionsProvider>
        <AdminShell>{children}</AdminShell>
      </SettingsActionsProvider>
    </AdminThemeProvider>
  );
}
